import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
    SidebarMenuItem as MenuItem,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SidebarMenuButton,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    useSidebar,
} from "@elit-hair/elit-ui";
import { useRouterState } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { NavItem } from "./navigation-items";
import { SidebarLink } from "./sidebar-link";

type SidebarMenuItemProps = NavItem;

type ChildrenMode = "popover" | "submenu";

function RenderChildren({
    mode,
    children,
    currentPath,
}: {
    mode: ChildrenMode;
    children: NavItem[];
    currentPath: string;
}) {
    const { t } = useTranslation();

    if (!children.length) return null;

    if (mode === "popover") {
        return (
            <div className="flex flex-col gap-1.5 p-1">
                {children.map((subItem) => {
                    const subActive = isPathActive(currentPath, subItem.to);
                    const ItemIcon = subItem.icon;

                    return (
                        <SidebarMenuButton key={`popover-${subItem.id}-${subItem.to}`} isActive={subActive} asChild>
                            <SidebarLink active={subActive} to={subItem.to || ""}>
                                {ItemIcon && <ItemIcon size={16} />}
                                <span>{t(subItem.title || "")}</span>
                            </SidebarLink>
                        </SidebarMenuButton>
                    );
                })}
            </div>
        );
    }

    return (
        <SidebarMenuSub>
            {children.map((subItem) => {
                const subActive = isPathActive(currentPath, subItem.to);
                const ItemIcon = subItem.icon;

                return (
                    <SidebarMenuSubItem key={`collapsible-${subItem.id}-${subItem.to}`}>
                        <SidebarMenuSubButton isActive={!!subActive} data-active={!!subActive} asChild>
                            <SidebarLink data-active={!!subActive} active={!!subActive} to={subItem.to || ""}>
                                {ItemIcon && <ItemIcon size={16} />}
                                <span>{t(subItem.title || "")}</span>
                            </SidebarLink>
                        </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                );
            })}
        </SidebarMenuSub>
    );
}

function SidebarSingleMenuItem(item: Omit<SidebarMenuItemProps, "children">) {
    const { state } = useSidebar();
    const isCollapsed = state === "collapsed";
    const Icon = item.icon;

    const content = (
        <MenuItem>
            <SidebarMenuButton asChild>
                <SidebarLink data-active={!!item.active} to={item.to} active={!!item.active}>
                    {Icon && <Icon size={16} />}
                    <span>{item.title}</span>
                </SidebarLink>
            </SidebarMenuButton>
        </MenuItem>
    );

    if (!isCollapsed) return content;

    return (
        <Tooltip>
            <TooltipTrigger>{content}</TooltipTrigger>
            <TooltipContent side="right" align="start" sideOffset={8}>
                {item.title}
            </TooltipContent>
        </Tooltip>
    );
}

export function SidebarMenuItem(props: SidebarMenuItemProps) {
    const { t } = useTranslation();
    const location = useRouterState({ select: (s) => s.location });
    const { state } = useSidebar();

    const isCollapsed = state === "collapsed";
    const currentPath = location?.pathname ?? "";

    const { title, icon, to } = props;
    const children = getChildren(props);

    const itemTitle = title ? t(title) : "";
    const ParentIcon = icon;

    const selfActive = isPathActive(currentPath, to);
    const childActive = children.some((c) => isPathActive(currentPath, c.to));
    const isActive = selfActive || childActive;

    const hasChildren = children.length > 0;

    if (!hasChildren) {
        return <SidebarSingleMenuItem title={itemTitle} icon={icon} to={to} active={isActive} />;
    }

    if (isCollapsed) {
        return (
            <Popover>
                <MenuItem data-active={isActive}>
                    <PopoverTrigger asChild>
                        <SidebarMenuButton data-active={isActive} isActive={isActive} tooltip={itemTitle}>
                            {ParentIcon && <ParentIcon />}
                            <span>{itemTitle}</span>
                        </SidebarMenuButton>
                    </PopoverTrigger>

                    <PopoverContent side="right" align="start" sideOffset={8}>
                        <RenderChildren mode="popover" children={children} currentPath={currentPath} />
                    </PopoverContent>
                </MenuItem>
            </Popover>
        );
    }

    return (
        <Collapsible asChild defaultOpen={isActive} className="group/collapsible">
            <MenuItem>
                <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={itemTitle}>
                        {ParentIcon && <ParentIcon />}
                        <span>{itemTitle}</span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                </CollapsibleTrigger>

                <CollapsibleContent>
                    <RenderChildren mode="submenu" children={children} currentPath={currentPath} />
                </CollapsibleContent>
            </MenuItem>
        </Collapsible>
    );
}

function normalizePath(p?: string) {
    if (!p) {
        return "";
    }

    let x = p.trim();

    if (!x.startsWith("/")) {
        x = "/" + x;
    }

    x = x.replace(/\/+$/, "");
    return x === "" ? "/" : x;
}

function isPathActive(currentPath: string, to?: string) {
    const cur = normalizePath(currentPath);
    const target = normalizePath(to);

    if (!target) {
        return false;
    }

    return cur === target || cur.startsWith(target + "/");
}

function getChildren(item: SidebarMenuItemProps) {
    return item.children ?? [];
}
